import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtUSD } from "../lib/format";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Coins,
  CalendarDays,
  BarChart3,
} from "lucide-react";
import ProfitSparkline from "../components/ProfitSparkline";
import { useAuth } from "../context/AuthContext";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};
const dayLabel = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

function DeltaBadge({ value }) {
  if (value == null)
    return (
      <span className="inline-flex items-center text-[10.5px] text-zinc-400 font-mono">
        <Minus size={9} strokeWidth={2} className="mr-0.5" />—
      </span>
    );
  const pos = value >= 0;
  const tone = pos
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-rose-700 bg-rose-50 border-rose-200";
  const Icon = value === 0 ? Minus : pos ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center text-[10.5px] font-mono px-1 py-0.5 rounded border ${tone}`}
    >
      <Icon size={9} strokeWidth={2} className="mr-0.5" />
      {pos ? "+" : ""}
      {value}%
    </span>
  );
}

export default function DailyProfit() {
  const { user } = useAuth();
  const cfOnly = !!user?.cf_team;
  const [windowDays, setWindowDays] = useState(cfOnly ? 2 : 30);
  const [windowMonths, setWindowMonths] = useState(12);
  const [profit, setProfit] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      // pull `windowDays` days ending today
      const end = todayISO();
      const startD = new Date();
      startD.setUTCDate(startD.getUTCDate() - (windowDays - 1));
      const start = startD.toISOString().slice(0, 10);
      const calls = [
        api.get("/profit", { params: { from: start, to: end } }),
      ];
      if (!cfOnly) {
        calls.push(api.get("/profit/monthly", { params: { months: windowMonths } }));
      }
      const results = await Promise.all(calls);
      setProfit(results[0].data);
      setMonthly(cfOnly ? null : results[1].data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [windowDays, windowMonths, cfOnly]);

  // Daily rows with day-over-day delta (skip null profit days)
  const dailyRows = useMemo(() => {
    const days = profit?.days || [];
    let prev = null;
    return days.map((d) => {
      const delta =
        d.profit_usd != null && prev != null && prev !== 0
          ? Math.round(((d.profit_usd - prev) / Math.abs(prev)) * 1000) / 10
          : null;
      const row = { ...d, delta };
      if (d.profit_usd != null) prev = d.profit_usd;
      return row;
    });
  }, [profit]);

  const stats = useMemo(() => {
    const knownDays = (profit?.days || []).filter(
      (d) => d.profit_usd != null
    );
    if (!knownDays.length)
      return {
        best: null,
        worst: null,
        avg: 0,
        upDays: 0,
        downDays: 0,
        flatDays: 0,
      };
    const best = knownDays.reduce(
      (a, b) => (b.profit_usd > a.profit_usd ? b : a),
      knownDays[0]
    );
    const worst = knownDays.reduce(
      (a, b) => (b.profit_usd < a.profit_usd ? b : a),
      knownDays[0]
    );
    const sum = knownDays.reduce((a, b) => a + b.profit_usd, 0);
    const upDays = knownDays.filter((d) => d.profit_usd > 0).length;
    const downDays = knownDays.filter((d) => d.profit_usd < 0).length;
    const flatDays = knownDays.length - upDays - downDays;
    return {
      best,
      worst,
      avg: sum / knownDays.length,
      upDays,
      downDays,
      flatDays,
      count: knownDays.length,
    };
  }, [profit]);

  const todays = profit?.days?.find((d) => d.is_today);

  return (
    <Layout
      title="Daily Profit"
      subtitle={
        cfOnly
          ? "CF Team view · today + yesterday only"
          : "Daily & monthly profit (USD), with up/down deltas"
      }
      testId="daily-profit-page"
      actions={
        !cfOnly && (
          <div className="flex gap-2">
            {[7, 30, 90].map((n) => (
              <button
                key={n}
                onClick={() => setWindowDays(n)}
                data-testid={`dp-window-${n}d`}
                className={`h-9 px-3 rounded-md border hairline text-[12.5px] font-mono ${
                  windowDays === n
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white hover:bg-zinc-50"
                }`}
              >
                {n}d
              </button>
            ))}
          </div>
        )
      }
    >
      {loading || !profit ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : cfOnly ? (
        <CFOnlyView profit={profit} dailyRows={dailyRows} />
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <Stat
              label="Today"
              value={todays?.profit_usd == null ? "—" : fmtUSD(todays.profit_usd)}
              accent={
                todays?.profit_usd == null
                  ? "muted"
                  : todays.profit_usd >= 0
                  ? "emerald"
                  : "rose"
              }
              sub={`Withdrawals ${fmtUSD(todays?.withdrawals_usd || 0)}`}
              testId="dp-today"
            />
            <Stat
              label={`Window · ${windowDays}d`}
              value={fmtUSD(profit.range_profit_usd || 0)}
              accent={(profit.range_profit_usd || 0) >= 0 ? "emerald" : "rose"}
              sub={`Sum of daily profits`}
              testId="dp-window-total"
            />
            <Stat
              label="All-time"
              value={fmtUSD(profit.all_time_profit_usd || 0)}
              accent={
                (profit.all_time_profit_usd || 0) >= 0 ? "emerald" : "rose"
              }
              sub={`${profit.snapshot_count || 0} snapshots`}
            />
            <Stat
              label="Best day"
              value={stats.best ? fmtUSD(stats.best.profit_usd) : "—"}
              accent="emerald"
              sub={stats.best ? dayLabel(stats.best.date) : ""}
              testId="dp-best-day"
            />
            <Stat
              label="Worst day"
              value={stats.worst ? fmtUSD(stats.worst.profit_usd) : "—"}
              accent="rose"
              sub={stats.worst ? dayLabel(stats.worst.date) : ""}
              testId="dp-worst-day"
            />
            <Stat
              label="Up · Down · Flat"
              value={
                <span className="text-[18px] font-mono">
                  <span className="text-emerald-700">{stats.upDays || 0}</span>
                  <span className="text-zinc-400"> / </span>
                  <span className="text-rose-700">{stats.downDays || 0}</span>
                  <span className="text-zinc-400"> / </span>
                  <span className="text-zinc-500">{stats.flatDays || 0}</span>
                </span>
              }
              sub={`${stats.count || 0} known days · avg ${fmtUSD(
                stats.avg || 0
              )}`}
              testId="dp-up-down-flat"
            />
          </div>

          {/* Sparkline */}
          {profit?.days?.length > 0 && (
            <div className="stat-card p-4 mb-5">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
                  <BarChart3
                    size={14}
                    strokeWidth={1.7}
                    className="text-indigo-600"
                  />
                  Trend · {windowDays} day{windowDays === 1 ? "" : "s"}
                </div>
                <div className="text-[11.5px] text-zinc-500 font-mono">
                  {profit.from} → {profit.to}
                </div>
              </div>
              <ProfitSparkline days={profit.days} />
            </div>
          )}

          {/* Daily breakdown table */}
          <div className="stat-card overflow-hidden mb-5">
            <div className="p-4 border-b hairline">
              <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
                <Coins
                  size={14}
                  strokeWidth={1.7}
                  className="text-amber-600"
                />
                Daily breakdown
              </div>
              <div className="text-[11.5px] text-zinc-500 mt-0.5">
                Profit = today closing − yesterday closing + today withdrawals (USD)
              </div>
            </div>
            <div className="overflow-x-auto max-h-[520px]">
              <table className="min-w-full text-[12.5px]">
                <thead className="sticky top-0 bg-zinc-50/80 backdrop-blur">
                  <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline">
                    <th className="py-2.5 px-4 font-medium">Date</th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Opening
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Closing
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Withdrawals
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Profit
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      vs Prev
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {[...dailyRows].reverse().map((d) => (
                    <tr
                      key={d.date}
                      className={`row-hover ${
                        d.is_today ? "bg-amber-50/40" : ""
                      }`}
                      data-testid={`dp-row-${d.date}`}
                    >
                      <td className="py-2.5 px-4 font-mono text-[12px]">
                        {d.date}
                        {d.is_today && (
                          <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            today
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-right">
                        {d.opening_usd == null ? "—" : fmtUSD(d.opening_usd)}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-right">
                        {d.closing_usd == null ? "—" : fmtUSD(d.closing_usd)}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-right text-rose-700">
                        {fmtUSD(d.withdrawals_usd)}
                      </td>
                      <td
                        className={`py-2.5 px-4 font-mono text-right font-medium ${
                          d.profit_usd == null
                            ? "text-zinc-400"
                            : d.profit_usd >= 0
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }`}
                      >
                        {d.profit_usd == null ? "—" : fmtUSD(d.profit_usd)}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <DeltaBadge value={d.delta} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly aggregation */}
          <div className="stat-card overflow-hidden">
            <div className="p-4 border-b hairline flex items-center justify-between">
              <div>
                <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
                  <CalendarDays
                    size={14}
                    strokeWidth={1.7}
                    className="text-indigo-600"
                  />
                  Monthly profit · MoM
                </div>
                <div className="text-[11.5px] text-zinc-500 mt-0.5">
                  Sum of daily profits per month
                </div>
              </div>
              <div className="flex gap-1.5">
                {[6, 12, 24].map((n) => (
                  <button
                    key={n}
                    onClick={() => setWindowMonths(n)}
                    data-testid={`dp-window-${n}m`}
                    className={`h-7 px-2 rounded-md border hairline text-[11px] font-mono ${
                      windowMonths === n
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white hover:bg-zinc-50"
                    }`}
                  >
                    {n}m
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12.5px]">
                <thead className="bg-zinc-50/50">
                  <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline">
                    <th className="py-2.5 px-4 font-medium">Month</th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Profit
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      vs Prev month
                    </th>
                    <th className="py-2.5 px-4 font-medium text-right">
                      Days w/ data
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {(monthly?.months || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-6 text-center text-zinc-500"
                      >
                        No snapshot data yet.
                      </td>
                    </tr>
                  ) : (
                    [...(monthly?.months || [])].reverse().map((m) => (
                      <tr
                        key={m.month}
                        className="row-hover"
                        data-testid={`dp-month-${m.month}`}
                      >
                        <td className="py-2.5 px-4 font-medium">
                          {monthLabel(m.month)}
                        </td>
                        <td
                          className={`py-2.5 px-4 font-mono text-right font-medium ${
                            m.profit_usd == null
                              ? "text-zinc-400"
                              : m.profit_usd >= 0
                              ? "text-emerald-700"
                              : "text-rose-700"
                          }`}
                        >
                          {m.profit_usd == null ? "—" : fmtUSD(m.profit_usd)}
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          <DeltaBadge value={m.delta} />
                        </td>
                        <td className="py-2.5 px-4 font-mono text-right text-zinc-500">
                          {m.days_with_data}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50/60">
                    <td className="py-2.5 px-4 text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Window total
                    </td>
                    <td
                      className={`py-2.5 px-4 font-mono text-right font-semibold ${
                        (monthly?.total_window_usd || 0) >= 0
                          ? "text-emerald-700"
                          : "text-rose-700"
                      }`}
                    >
                      {fmtUSD(monthly?.total_window_usd || 0)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

function CFOnlyView({ profit, dailyRows }) {
  const todays = profit?.days?.find((d) => d.is_today);
  // Yesterday is the day before today's entry; pick from dailyRows
  const sorted = [...dailyRows];
  const todayIdx = sorted.findIndex((d) => d.is_today);
  const yest = todayIdx > 0 ? sorted[todayIdx - 1] : null;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div className="stat-card p-5" data-testid="dp-today">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Today
          </div>
          <div
            className={`font-mono text-[28px] font-medium mt-2 ${
              todays?.profit_usd == null
                ? "text-zinc-400"
                : todays.profit_usd >= 0
                ? "text-emerald-700"
                : "text-rose-700"
            }`}
          >
            {todays?.profit_usd == null ? "—" : fmtUSD(todays.profit_usd)}
          </div>
          <div className="text-[11.5px] text-zinc-500 mt-1 font-mono">
            Withdrawals {fmtUSD(todays?.withdrawals_usd || 0)}
          </div>
        </div>
        <div className="stat-card p-5" data-testid="dp-yesterday">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Yesterday
          </div>
          <div
            className={`font-mono text-[28px] font-medium mt-2 ${
              yest?.profit_usd == null
                ? "text-zinc-400"
                : yest.profit_usd >= 0
                ? "text-emerald-700"
                : "text-rose-700"
            }`}
          >
            {yest?.profit_usd == null ? "—" : fmtUSD(yest.profit_usd)}
          </div>
          <div className="text-[11.5px] text-zinc-500 mt-1 font-mono">
            Withdrawals {fmtUSD(yest?.withdrawals_usd || 0)}
          </div>
        </div>
      </div>
      <div className="stat-card overflow-hidden">
        <div className="p-4 border-b hairline">
          <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
            <Coins size={14} strokeWidth={1.7} className="text-amber-600" />
            Today &amp; Yesterday
          </div>
        </div>
        <table className="min-w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
              <th className="py-2.5 px-4 font-medium">Date</th>
              <th className="py-2.5 px-4 font-medium text-right">
                Withdrawals
              </th>
              <th className="py-2.5 px-4 font-medium text-right">Profit</th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {[todays, yest]
              .filter(Boolean)
              .map((d) => (
                <tr
                  key={d.date}
                  className={`row-hover ${d.is_today ? "bg-amber-50/40" : ""}`}
                  data-testid={`dp-row-${d.date}`}
                >
                  <td className="py-2.5 px-4 font-mono text-[12.5px]">
                    {d.date}
                    {d.is_today && (
                      <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        today
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 font-mono text-right text-rose-700">
                    {fmtUSD(d.withdrawals_usd)}
                  </td>
                  <td
                    className={`py-2.5 px-4 font-mono text-right font-medium ${
                      d.profit_usd == null
                        ? "text-zinc-400"
                        : d.profit_usd >= 0
                        ? "text-emerald-700"
                        : "text-rose-700"
                    }`}
                  >
                    {d.profit_usd == null ? "—" : fmtUSD(d.profit_usd)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Stat({ label, value, sub, accent, testId }) {
  const cls =
    {
      emerald: "text-emerald-700",
      rose: "text-rose-700",
      amber: "text-amber-700",
      muted: "text-zinc-400",
    }[accent || ""] || "text-zinc-900";
  return (
    <div className="stat-card p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className={`font-mono text-[20px] font-medium mt-1 ${cls}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
          {sub}
        </div>
      )}
    </div>
  );
}
